<template>
  <!-- Scrolls inside the sheet: it's capped below the status bar, and without this the
       overflow (save CTA included) was clipped under the Android navigation bar. -->
  <div class="task-editor flex flex-col gap-4 p-4 pb-6 min-h-0 overflow-y-auto overscroll-contain [&>*]:shrink-0" dir="rtl">
    <div v-if="task && chip" class="flex gap-1.5">
      <span class="chip chip-auto">{{ chip }}</span>
    </div>

    <label class="flex flex-col gap-1.5">
      <span class="field-label">מה צריך לעשות?</span>
      <InputText
        ref="titleInput"
        v-model="form.title"
        class="w-full !h-12 !text-base"
        placeholder="לדוגמה: להזמין צבע מהספק"
        maxlength="300"
        @keydown.enter="save"
      />
    </label>

    <div v-if="showNotes" class="flex flex-col gap-1.5">
      <label class="field-label" for="task-notes">פרטים</label>
      <Textarea id="task-notes" v-model="form.notes" rows="3" class="w-full text-sm" auto-resize maxlength="2000" />
    </div>
    <button v-else type="button" class="text-sm text-primary-700 font-medium self-start" @click="showNotes = true">+ הוספת פרטים</button>

    <div class="flex flex-col gap-2">
      <span class="field-label">מתי?</span>
      <!-- One scrolling row, bleeding to the sheet's edges -->
      <div class="chip-scroller -mx-4 px-4">
        <button
          v-for="option in dateOptions"
          :key="option.key"
          type="button"
          class="date-chip"
          :class="{ active: option.active }"
          @click="option.pick"
        >{{ option.label }}</button>
        <button
          type="button"
          class="date-chip"
          :class="{ active: isCustomDate || showCalendar }"
          :aria-expanded="showCalendar"
          @click="showCalendar = !showCalendar"
        >
          {{ isCustomDate ? customDateLabel : 'תאריך אחר' }}
          <i :class="['pi !text-[10px] ms-1.5', showCalendar ? 'pi-chevron-up' : 'pi-chevron-down']" />
        </button>
      </div>
      <!-- Inline, not a drawer: this sheet may already be the second drawer (from an appointment). -->
      <div v-if="showCalendar" class="border border-surface-200 rounded-[14px] p-3">
        <DateGridCalendar :model-value="dueDateObject" :min-date="minDateObject" @update:model-value="onCalendarPick" />
      </div>
    </div>

    <div class="rows">
      <div v-if="form.dueDate" class="row">
        <i class="pi pi-clock" aria-hidden="true" />
        <span class="flex-1">שעה</span>
        <template v-if="form.dueTime != null">
          <div class="time-cell">
            <MobileTimePicker v-model="timeModel" is24Hour placeholder="בחרו שעה" />
          </div>
          <button type="button" class="w-6 flex justify-center text-surface-400" aria-label="הסרת השעה" @click="clearTime">
            <i class="pi pi-times !text-xs" />
          </button>
        </template>
        <button v-else type="button" class="text-sm text-primary-700 font-medium px-1" @click="addTime">הוספת שעה</button>
      </div>

      <!-- Reserving calendar time needs a start time, so it only appears once one is set. -->
      <div v-if="form.dueDate && form.dueTime != null" class="row !items-start !py-3">
        <i class="pi pi-calendar mt-0.5" aria-hidden="true" />
        <div class="flex-1 flex flex-col gap-0.5">
          <span>לשריין זמן ביומן</span>
          <span class="text-xs text-surface-500">חוסם את השעה לקביעת תורים</span>
          <div v-if="reserve" class="flex gap-1.5 flex-wrap mt-2" role="radiogroup" aria-label="משך השריון">
            <button
              v-for="option in reserveOptions"
              :key="option.value"
              type="button"
              role="radio"
              :aria-checked="reserveMinutes === option.value"
              class="length-chip"
              :class="{ active: reserveMinutes === option.value }"
              @click="reserveMinutes = option.value"
            >{{ option.label }}</button>
          </div>
        </div>
        <ToggleSwitch v-model="reserve" aria-label="לשריין זמן ביומן" />
      </div>

      <div v-if="!lockCustomer" class="row cursor-pointer" role="button" tabindex="0" @click="pickCustomer" @keydown.enter="pickCustomer">
        <i class="pi pi-user" aria-hidden="true" />
        <span class="flex-1">לקוח</span>
        <span v-if="form.customerId" class="chip chip-customer !text-sm !px-2.5 !py-1">{{ customerName }}</span>
        <span v-else class="text-sm text-surface-500">בחירה</span>
        <button v-if="form.customerId" type="button" class="p-1 text-surface-500" aria-label="הסרת לקוח" @click.stop="clearCustomer">
          <i class="pi pi-times text-xs" />
        </button>
      </div>

      <div v-if="canManage && assigneeOptions.length > 1" class="row">
        <i class="pi pi-users" aria-hidden="true" />
        <span class="flex-1">באחריות</span>
        <Select v-model="assigneeModel" :options="assigneeOptions" option-label="label" option-value="value" :pt="rowSelectPt" aria-label="באחריות" />
      </div>

      <div v-if="form.dueDate" class="row stacked">
        <div class="flex items-center gap-3">
          <i class="pi pi-bell" aria-hidden="true" />
          <span id="task-reminder-label">תזכורת</span>
        </div>
        <div class="chip-scroller -mx-3.5 px-3.5" role="radiogroup" aria-labelledby="task-reminder-label">
          <button
            v-for="option in reminderOptions"
            :key="option.value"
            type="button"
            role="radio"
            :aria-checked="reminder === option.value"
            class="length-chip"
            :class="{ active: reminder === option.value }"
            @click="reminder = option.value"
          >{{ option.label }}</button>
        </div>
      </div>

      <div v-if="form.dueDate" class="row stacked">
        <div class="flex items-center gap-3">
          <i class="pi pi-refresh" aria-hidden="true" />
          <span id="task-repeat-label">חזרה</span>
        </div>
        <div class="chip-scroller -mx-3.5 px-3.5" role="radiogroup" aria-labelledby="task-repeat-label">
          <button
            v-for="option in recurrenceOptions"
            :key="option.value"
            type="button"
            role="radio"
            :aria-checked="recurrenceModel === option.value"
            class="length-chip"
            :class="{ active: recurrenceModel === option.value }"
            @click="recurrenceModel = option.value"
          >{{ option.label }}</button>
        </div>
      </div>

    </div>

    <small v-if="error" class="text-red-600">{{ error }}</small>

    <div class="flex flex-col gap-2.5">
      <Button label="שמירת משימה" class="!h-12 !rounded-[14px] !text-base !font-bold" :loading="saving" :disabled="!form.title.trim()" @click="save" />

      <div v-if="task?.id" class="flex gap-2.5">
        <button type="button" class="action-btn action-done flex-1" :disabled="toggling" @click="toggleDone">
          <i :class="['pi', toggling ? 'pi-spinner pi-spin' : done ? 'pi-replay' : 'pi-check']" aria-hidden="true" />
          {{ done ? 'החזרה לפתוחות' : 'סימון כבוצע' }}
        </button>
        <button
          v-if="canDelete"
          type="button"
          class="action-btn action-delete"
          :class="{ confirming: confirmDelete }"
          :aria-label="confirmDelete ? 'אישור מחיקה' : 'מחיקת המשימה'"
          :disabled="deleting"
          @click="remove"
        >
          <i :class="['pi', deleting ? 'pi-spinner pi-spin' : 'pi-trash']" aria-hidden="true" />
          <span v-if="confirmDelete">למחוק?</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, reactive, ref, watch } from 'vue';
import { DateTime } from 'luxon';
import { useToast } from 'vue-toastification';
import {
  type ICustomer,
  type ITask,
  type ITaskInput,
  TaskRecurrenceEnum,
  TaskSourceEnum,
  TaskStatusEnum,
  TASK_RECURRENCE_LABELS,
} from '@torim-app/types';
import Select from 'primevue/select';
import Textarea from 'primevue/textarea';
import ToggleSwitch from 'primevue/toggleswitch';
import { DateGridCalendar, useDrawer } from '@torim-app/shared-components';
import MobileTimePicker from '@app/components/admin/components/MobileTimePicker.vue';
import { useAdminStore, useAdminTasksStore } from '@app/stores';
import { trackEvent } from '@app/composables/posthog';
import SelectCustomerDrawerContent from '@app/components/admin/components/SelectCustomerDrawerContent.vue';
import {
  formatHour,
  isoPlusDays,
  REMINDER_OPTIONS,
  type ReminderPreset,
  reminderAt,
  reminderPresetOf,
  sourceChip,
  todayIso,
} from './task-utils';

const RESERVE_LENGTHS = [15, 30, 45, 60, 90, 120, 180];

const props = withDefaults(defineProps<{
  /** Editing an existing task; omitted to create one. */
  task?: ITask;
  /** Prefill for a new task (from a customer page, an appointment, a calendar day). */
  defaults?: Partial<ITaskInput> & { customerName?: string };
  /** Customer / appointment context fixes the customer. */
  lockCustomer?: boolean;
  /** Close the surrounding drawer after saving. */
  closeOnSave?: boolean;
}>(), { closeOnSave: true });

const emit = defineEmits<{
  saved: [task: ITask];
  removed: [id: string];
}>();

const toast = useToast();
const { openDrawer, closeDrawer } = useDrawer();
const adminStore = useAdminStore();
const tasksStore = useAdminTasksStore();

const canManage = computed(() => adminStore.getCanManageOthers);
const source = props.task ?? props.defaults ?? {};

const form = reactive<ITaskInput & { title: string }>({
  title: props.task?.title ?? props.defaults?.title ?? '',
  notes: props.task?.notes ?? props.defaults?.notes ?? '',
  dueDate: props.task ? props.task.dueDate ?? null : (props.defaults?.dueDate !== undefined ? props.defaults.dueDate : todayIso()),
  dueTime: source.dueTime ?? null,
  assigneeId: props.task ? props.task.assigneeId ?? null : (props.defaults?.assigneeId ?? (canManage.value ? null : adminStore.getUserId)),
  customerId: source.customerId ?? null,
  appointmentId: source.appointmentId ?? undefined,
  recurrence: (source.recurrence as TaskRecurrenceEnum | null) ?? null,
});
const customerName = ref(props.task?.customerName ?? props.defaults?.customerName ?? '');
const showNotes = ref(!!form.notes);
const reminder = ref<ReminderPreset>(props.task ? reminderPresetOf(props.task) : 'none');
const reserve = ref(!!props.task?.reservation);
const reserveMinutes = ref(props.task?.reservation ? Math.round((props.task.reservation.to - props.task.reservation.from) * 60) : 30);
const saving = ref(false);
const toggling = ref(false);
const deleting = ref(false);
const confirmDelete = ref(false);
const error = ref('');
const titleInput = ref();

const done = computed(() => props.task?.status === TaskStatusEnum.Done);
const chip = computed(() => (props.task ? sourceChip(props.task) : null));
// Automatic and checklist tasks come back if deleted, so they're closed instead.
const canDelete = computed(() => props.task?.source === TaskSourceEnum.Manual);
const minDate = computed(() => (props.task?.dueDate && props.task.dueDate < todayIso() ? props.task.dueDate : todayIso()));

const setDate = (iso: string | null) => {
  form.dueDate = iso;
  if (!iso) {
    form.dueTime = null;
    form.recurrence = null;
    reserve.value = false;
    reminder.value = 'none';
  }
};

const dateOptions = computed(() => [
  { key: 'today', label: 'היום', iso: todayIso() },
  { key: 'tomorrow', label: 'מחר', iso: isoPlusDays(1) },
  { key: 'week', label: 'בעוד שבוע', iso: isoPlusDays(7) },
  { key: 'none', label: 'בלי תאריך', iso: null },
].map(o => ({ ...o, active: form.dueDate === o.iso, pick: () => { setDate(o.iso); showCalendar.value = false; } })));

const isCustomDate = computed(() => !!form.dueDate && !dateOptions.value.some(o => o.active));
const customDateLabel = computed(() => (form.dueDate ? DateTime.fromISO(form.dueDate).setLocale('he').toFormat('cccc d.M') : ''));

// "בשעת המשימה" only means something once the task has a time.
const reminderOptions = computed(() => REMINDER_OPTIONS.filter(o => o.value !== 'at-time' || form.dueTime != null));

const showCalendar = ref(false);
const dueDateObject = computed(() => (form.dueDate ? DateTime.fromISO(form.dueDate).toJSDate() : null));
const minDateObject = computed(() => DateTime.fromISO(minDate.value).toJSDate());
const onCalendarPick = (value: Date | Array<Date | null>) => {
  if (!(value instanceof Date)) return;
  setDate(DateTime.fromJSDate(value).toISODate());
  showCalendar.value = false;
};

// MobileTimePicker works on a Date; the task keeps a decimal hour.
const timeModel = computed<Date>({
  get: () => {
    const [h, m] = formatHour(form.dueTime ?? 0).split(':').map(Number);
    const d = new Date();
    d.setHours(h, m, 0, 0);
    return d;
  },
  set: (value: Date) => {
    if (value) form.dueTime = value.getHours() + value.getMinutes() / 60;
  },
});
const addTime = () => {
  // Next whole hour, within the day.
  form.dueTime = Math.min(DateTime.local({ zone: 'Asia/Jerusalem' }).hour + 1, 23);
};
const clearTime = () => {
  form.dueTime = null;
  reserve.value = false;
};

watch(() => form.dueTime, (time) => {
  if (time == null && reminder.value === 'at-time') reminder.value = 'morning';
});

// PrimeVue Select reads a null value as "nothing selected", so the null choices
// (owner / no repeat) go through sentinel values.
const OWNER = 'owner';
const NO_REPEAT = 'none';

const assigneeOptions = computed(() => {
  const providers = (adminStore.getProviders ?? []) as Array<{ userId: string; fullName: string }>;
  return [
    { value: OWNER, label: 'בעל העסק' },
    ...providers.map(p => ({ value: p.userId, label: p.userId === adminStore.getUserId ? `${p.fullName} (אני)` : p.fullName })),
  ];
});
const assigneeModel = computed({
  get: () => form.assigneeId ?? OWNER,
  set: (value: string) => { form.assigneeId = value === OWNER ? null : value; },
});

const recurrenceOptions = [
  { value: NO_REPEAT, label: 'לא חוזרת' },
  ...Object.entries(TASK_RECURRENCE_LABELS).map(([value, label]) => ({ value, label })),
];
const recurrenceModel = computed<string>({
  get: () => form.recurrence ?? NO_REPEAT,
  set: (value: string) => { form.recurrence = value === NO_REPEAT ? null : value as TaskRecurrenceEnum; },
});

// Short labels — they sit in a row of chips.
const reserveOptions = RESERVE_LENGTHS.map(m => ({
  value: m,
  label: m < 60 ? `${m} דק׳` : m === 60 ? 'שעה' : m === 120 ? 'שעתיים' : `${m / 60} שעות`,
}));

/** A borderless Select that sits at the end of a settings row. */
const rowSelectPt = {
  root: { class: '!border-0 !shadow-none !bg-transparent' },
  label: { class: '!text-sm !text-surface-600 !py-1 !px-1' },
  dropdown: { class: '!w-6 !text-surface-400' },
};

const pickCustomer = () => {
  openDrawer(SelectCustomerDrawerContent, {
    initialCustomerId: form.customerId ?? undefined,
    onCustomerSelected: (customer: Pick<ICustomer, 'id' | 'fullName'>) => {
      form.customerId = customer.id;
      customerName.value = customer.fullName;
    },
    onCancel: () => closeDrawer(),
  }, { title: 'בחירת לקוח', noPadding: true });
};

const clearCustomer = () => {
  form.customerId = null;
  customerName.value = '';
};

const payload = (): ITaskInput => ({
  ...form,
  title: form.title.trim(),
  reminderAt: reminderAt(reminder.value, form.dueDate, form.dueTime),
  reserveMinutes: reserve.value && form.dueTime != null ? reserveMinutes.value : 0,
});

const save = async () => {
  if (!form.title.trim() || saving.value) return;
  saving.value = true;
  error.value = '';
  try {
    const saved = props.task?.id
      ? await tasksStore.update(props.task.id, payload(), !!props.task.reservation)
      : await tasksStore.create(payload());
    trackEvent(props.task?.id ? 'task_updated' : 'task_created', {
      hasDate: !!form.dueDate, hasCustomer: !!form.customerId, reserved: reserve.value, recurring: !!form.recurrence,
    });
    emit('saved', saved);
    if (props.closeOnSave) closeDrawer();
  } catch (e) {
    console.error(e);
    error.value = 'לא הצלחנו לשמור את המשימה. נסו שוב.';
  } finally {
    saving.value = false;
  }
};

const toggleDone = async () => {
  if (!props.task?.id) return;
  toggling.value = true;
  try {
    const saved = await tasksStore.setDone(props.task.id, !done.value);
    emit('saved', saved);
    if (props.closeOnSave) closeDrawer();
  } catch (e) {
    console.error(e);
    toast.error('לא הצלחנו לעדכן את המשימה');
  } finally {
    toggling.value = false;
  }
};

const remove = async () => {
  if (!props.task?.id) return;
  if (!confirmDelete.value) {
    confirmDelete.value = true;
    return;
  }
  deleting.value = true;
  try {
    await tasksStore.remove(props.task.id, !!props.task.reservation);
    trackEvent('task_deleted');
    emit('removed', props.task.id);
    if (props.closeOnSave) closeDrawer();
  } catch (e) {
    console.error(e);
    toast.error('לא הצלחנו למחוק את המשימה');
  } finally {
    deleting.value = false;
  }
};

onMounted(() => {
  if (!props.task) nextTick(() => titleInput.value?.$el?.focus?.());
});
</script>

<style scoped lang="scss">
.field-label {
  @apply text-[13px] font-semibold text-surface-600;
}

.chip-scroller {
  @apply flex gap-2 overflow-x-auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  > * {
    @apply flex-none whitespace-nowrap;
  }
}

.row.stacked {
  @apply flex-col items-stretch gap-2.5 py-3;
}

.action-btn {
  @apply h-12 rounded-[14px] text-[15px] font-semibold flex items-center justify-center gap-2 transition-colors;

  &:disabled {
    @apply opacity-60;
  }
}

.action-done {
  @apply bg-primary-50 text-primary-700;
}

.action-delete {
  @apply w-12 flex-none bg-red-50 text-red-600;

  &.confirming {
    @apply w-auto px-4 bg-red-600 text-white;
  }
}

.length-chip {
  @apply h-8 px-3 rounded-full border border-surface-200 bg-white text-[13px] text-surface-700;

  &.active {
    @apply border-primary bg-primary-50 text-primary-700 font-semibold;
  }
}

.date-chip {
  @apply h-10 px-3.5 rounded-full border border-surface-200 bg-white text-sm text-surface-900 flex items-center cursor-pointer;

  &.active {
    @apply border-primary bg-primary-50 text-primary-700 font-semibold;
  }
}

.rows {
  @apply flex flex-col border border-surface-200 rounded-[14px] overflow-hidden;
}

.row {
  @apply min-h-[52px] px-3.5 flex items-center gap-3 border-b border-surface-100 text-[15px] text-surface-900;

  &:last-child {
    @apply border-b-0;
  }

  > .pi {
    @apply text-surface-500 text-base;
  }
}

.chip {
  @apply text-[11px] font-semibold rounded-full px-2 py-0.5;
}

// The shared time picker draws a boxed input; in a settings row it should read like the
// row's other values (plain grey text, no frame).
.time-cell {
  @apply w-[4.5rem] flex-none;

  :deep(input) {
    border: 0 !important;
    box-shadow: none !important;
    background: transparent !important;
    padding: 0.25rem !important;
    font-size: 0.875rem !important;
    line-height: 1.25rem;
    color: rgb(var(--surface-600)) !important;
    text-align: left !important;
    cursor: pointer;
  }

  :deep(.input-icon),
  :deep(.p-datepicker-input-icon-container) {
    display: none;
  }
}

.chip-auto {
  background: #ffedd5;
  color: #9a3412;
}

.chip-customer {
  background: #d9f3f4;
  color: #0b5d65;
}
</style>
